import { useEffect, useState, useRef } from "react"
import { useLocation, useNavigate } from "react-router"
import { useQueryClient } from "@tanstack/react-query"
import cx from "classnames"
import { CheckoutProvider } from "@stripe/react-stripe-js"
import { loadStripe, Appearance } from "@stripe/stripe-js"

import MainLayout from "./_layouts/Main"
import { Spinner } from "../components/Spinner"
import { Toast } from "../components/Toast"
import { ButtonPair } from "../components/ButtonPair"
import { genericErrMsg } from "../api/auth"
import {
  MonthlyStatementBulletPoints,
  MappingBulletPoints,
} from "../sections/Paywall/constants"
import PaywallSplitSection from "../sections/Paywall/PaywallSplitSection"
import PaywallPaymentForm from "../sections/Paywall/PaywallPaymentForm"
import PaywallAdvisorIntro from "../sections/Paywall/PaywallAdvisorIntro"
import PaywallTestimonialSlider from "../sections/Paywall/PaywallTestimonialSlider"
import MembershipBenefitsExplainerSection from "../sections/Paywall/MembershipBenefits/MembershipBenefitsExplainerSection"
import ServicesLearnMoreLink from "../sections/Paywall/MembershipBenefits/ServicesLearnMoreLink"
import PaywallAboutNCX from "../sections/Paywall/PaywallAboutNCX"
import PaywallFooter from "../sections/Paywall/PaywallFooter"
import useMediaQuery from "../hooks/useMediaQuery"
import GREEN_BULLET from "../images/green-bullet.svg"
import GREEN_BLOB from "../images/green-blob.png"
import MONTHLY_STMT from "../images/monthly-statement-all.png"
import MONTHLY_STMT_MOB from "../images/monthly-statement-all-mobile.png"
import POWERFUL_MAPPING from "../images/paywall-powerful-mapping.png"
import POWERFUL_MAPPING_MOB from "../images/paywall-powerful-mapping-mobile.png"
import FORESTER from "../images/paywall-forester.png"

import {
  useAccount,
  useAccountId,
  useCreateStripeSession,
  useProfile,
} from "../hooks"
import { Profile } from "@/types"

const STRIPE_KEY = import.meta.env.VITE_APP_STRIPE_KEY as string

const stripe = STRIPE_KEY
  ? loadStripe(STRIPE_KEY, {
      betas: ["custom_checkout_server_updates_1"],
      developerTools: {
        assistant: { enabled: false },
      },
    })
  : null

const Paywall = () => {
  const accountId = useAccountId()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const [stripeIsLoading, setStripeIsLoading] = useState(true)
  const { scrollToTop, joinBtnClass, prevPath } = location.state ?? {
    prevPath: "/",
  }
  const benefitsRef = useRef<HTMLDivElement>(null)
  const isMaxWidthXs = useMediaQuery("(max-width: 576px)")
  const isMobile = useMediaQuery("(max-width: 1024px)")

  const formattedPrevPath = prevPath?.startsWith("/")
    ? prevPath.substring(1)
    : prevPath

  const { data: account, isLoading: accountIsLoading } = useAccount(
    queryClient,
    accountId
  )

  const { data: profile, isLoading: profileIsLoading } = useProfile<
    Profile,
    Error
  >(queryClient)

  const firstName = profile?.first_name ?? ""

  const { mutateAsync: createStripeSession } = useCreateStripeSession(
    queryClient,
    accountId
  )

  const fetchClientSecret = () => {
    setStripeIsLoading(true)
    // initialize checkout session with monthly pricing
    return createStripeSession({
      lookup_key: "basic_monthly",
      redirect_path: formattedPrevPath,
      embedded_checkout: true,
    })
      .then((data) => {
        const typedData = data as { clientSecret: string }
        setStripeIsLoading(false)
        return typedData.clientSecret
      })
      .catch(() => {
        setStripeIsLoading(false)
        Toast.error(genericErrMsg)
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        return Promise.reject(genericErrMsg)
      })
  }

  const scrollToBenefitsSection = () => {
    benefitsRef.current?.scrollIntoView({
      behavior: "smooth",
    })
  }

  const stripeFormAppearance: Appearance = {
    theme: "stripe",
    variables: {
      colorPrimary: "#416952",
      colorText: "#818181",
      fontFamily: "proxima-nova, system-ui, sans-serif",
      colorBackground: "#FFFFFF",
      accessibleColorOnColorPrimary: "#262626",
    },
    rules: {
      ".Block": {
        backgroundColor: "var(--colorBackground)",
        boxShadow: "none",
        padding: "12px",
      },
      ".Input:disabled, .Input--invalid:disabled": {
        color: "var(--colorBackground)",
      },
      ".Label": {
        fontWeight: "700",
        fontSize: "16px",
      },
    },
  }

  useEffect(() => {
    if (scrollToTop) {
      window.scrollTo(0, 0)
    }
  }, [scrollToTop])

  if (account?.is_member) {
    navigate("../", { replace: true })

    return (
      <div className="flex-auto flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <CheckoutProvider
      stripe={stripe}
      options={{
        fetchClientSecret: fetchClientSecret,
        elementsOptions: {
          appearance: stripeFormAppearance,
        },
      }}
    >
      <MainLayout
        isLoading={[accountIsLoading, stripeIsLoading, profileIsLoading]}
        contentBg="bg-white"
        className="personal-advisor-paywall overflow-x-hidden"
      >
        <div className="container max-w-[1296px] pt-[30px] lg:pt-[58px] lg:pb-[132px]">
          <h1
            className="text-3xl lg:text-4xl text-black text-center font-display font-medium leading-[124%] tracking-[0.6px] lg:tracking-[0.72px] -mx-5"
            data-testid="paywall-header"
          >
            Become a member today
          </h1>

          <h2
            className="text-autumn text-2xl lg:text-3xl text-center font-medium leading-[124%] tracking-[0.48px] lg:tracking-[0.6px] mt-2 lg:mt-[10px] mb-9 lg:mb-17"
            data-testid="paywall-subheader"
          >
            Meet your personal advisor from NCX
          </h2>

          <div className="flex flex-col lg:flex-row lg:gap-[62px]">
            <PaywallAdvisorIntro
              firstName={firstName}
              onClickBenefits={scrollToBenefitsSection}
              isMobile={isMobile}
            />

            <div className="w-px bg-[#D9D9D9] hidden md:block" />

            <div className="shrink-0 w-full lg:w-[390px] lg:pl-[47px] mt-18 lg:mt-0">
              <PaywallPaymentForm
                joinBtnClass={cx(
                  "become-a-member",
                  "personal-advisor-paywall",
                  joinBtnClass as string
                )}
              />
            </div>
          </div>

          {isMobile ? (
            <div className="relative flex flex-col items-center my-9">
              <div className="absolute z-0 inset-0 flex justify-center items-center">
                <img
                  src={GREEN_BLOB}
                  alt=""
                  className="w-xs md:h-[295px] mr-3 mb-24"
                  aria-hidden="true"
                  data-testid="paywall-green-blob"
                />
              </div>

              <div className="relative z-2 w-full flex flex-col items-center">
                <p
                  className="max-w-[400px] text-center text-black text-2xl font-display font-medium leading-[124%] tracking-[0.48px]"
                  data-testid="paywall-benefits-text"
                >
                  Read more about all our membership benefits below
                </p>

                <div className="flex flex-col justify-evenly sm:flex-row gap-6 w-full mb-24">
                  <ButtonPair
                    className="mt-6 !flex-col-reverse"
                    primary={
                      <button
                        className={
                          "member-widget-dashboard-link personal-advisor-paywall whitespace-nowrap self-center w-auto"
                        }
                        onClick={() => {
                          navigate("/")
                        }}
                        data-testid="paywall-go-to-dashboard-button"
                      >
                        No, thanks. Go to my dashboard
                      </button>
                    }
                    secondary={
                      <button
                        className="benefits-button personal-advisor-paywall btn2 bg-white border border-color-dusk-100 font-semibold self-center w-auto"
                        onClick={scrollToBenefitsSection}
                        data-testid="paywall-benefits-button"
                      >
                        Membership Benefits
                      </button>
                    }
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <PaywallTestimonialSlider />

        <div ref={benefitsRef} className="w-full bg-sand pt-12 md:pt-24">
          <h1
            className="text-black text-3xl md:text-4xl font-medium text-center leading-[124%] tracking-[0.6px] md:tracking-[1px]"
            data-testid="paywall-benefits-heading"
          >
            Membership Benefits
          </h1>
        </div>

        <PaywallSplitSection
          leftContent={
            <img
              src={isMaxWidthXs ? MONTHLY_STMT_MOB : MONTHLY_STMT}
              alt="Monthly statement"
              className="w-full h-auto block"
              data-testid="paywall-monthly-statement-image"
            />
          }
          rightContent={
            <MembershipBenefitsExplainerSection
              overline="Timely check-up"
              headline="Monthly Statements"
              subheading="We look out for you and your land."
              body={
                <div className="flex flex-col space-y-3">
                  {MonthlyStatementBulletPoints.map(
                    (item: string, index: number) => (
                      <div
                        key={index}
                        className="inline-flex items-start gap-4"
                      >
                        <img src={GREEN_BULLET} alt="" className="size-6" />
                        <div className="text-gray-800 font-medium justify-start">
                          {item}
                        </div>
                      </div>
                    )
                  )}
                </div>
              }
            />
          }
        />

        <PaywallSplitSection
          leftContent={
            <img
              src={isMaxWidthXs ? POWERFUL_MAPPING_MOB : POWERFUL_MAPPING}
              alt="Powerful mapping"
              className="w-full h-auto block"
              data-testid="paywall-powerful-mapping-image"
            />
          }
          rightContent={
            <MembershipBenefitsExplainerSection
              overline="Data at your fingertips"
              headline="Powerful Mapping and Insights"
              subheading="NCX Members get access to advanced features on the NCX platform including:"
              body={
                <div className="flex flex-col space-y-3">
                  {MappingBulletPoints.map((item: string, index: number) => (
                    <div key={index} className="inline-flex items-start gap-4">
                      <img src={GREEN_BULLET} alt="" className="size-6" />
                      <div className="text-gray-800 font-medium justify-start">
                        {item}
                      </div>
                    </div>
                  ))}
                </div>
              }
            />
          }
        />

        <PaywallSplitSection
          leftContent={
            <img
              src={FORESTER}
              alt="Forester"
              className="w-full h-auto block"
              data-testid="paywall-forester-image"
            />
          }
          rightContent={
            <MembershipBenefitsExplainerSection
              overline="More than just our platform"
              headline="Optional Add-on Services"
              subheading="Your NCX membership isn't a replacement for a local forester."
              body={
                <div className="space-y-4">
                  <p className="text-lg font-normal leading-relaxed tracking-tight">
                    For big decision, long-term planning, or on-the-ground
                    implementation, NCX Members have access to the fee-based
                    services including:
                  </p>

                  <ul className="list-disc pl-6 mb-6 space-y-1">
                    <li>transaction advisory</li>

                    <li>forest management plans</li>

                    <li>timber inventory & sales</li>

                    <li>program application assistance</li>

                    <li>and more</li>
                  </ul>
                  <ServicesLearnMoreLink />
                </div>
              }
            />
          }
        />

        <PaywallAboutNCX />

        <PaywallFooter />
      </MainLayout>
    </CheckoutProvider>
  )
}

export default Paywall
